import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import { useHistory, useParams } from "react-router-dom";
import UploadBox from "../../../../shared/molecules/fields/UploadBox";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { GET_PRESIGNED_URL } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { useMutation } from "@apollo/client";
import { CREATE_EXAM_PAPER } from "../../../../utils/graphql/services/ai-evaluation/mutations";
import { Alert, Snackbar } from "@mui/material";

type CreateExamInput = {
  title: string;
  questionPaperPublicPdfUrl: string;
  scheduledDate: string | null; // Changed to string
  totalSections: number;
  totalMarks: number;
  totalQuestions: number;
  questionTypes: string[];
  bloomsTaxonomyOne: string[];
  bloomsTaxonomyTwo: string[];
  isAutogradeEnabled: boolean;
  isAutomarksEnabled: boolean;
  isRoundingEnabled: boolean;
};

const DEFAULT_INPUT: CreateExamInput = {
  title: "",
  questionPaperPublicPdfUrl: "",
  scheduledDate: null,
  totalSections: 0,
  totalMarks: 0,
  totalQuestions: 0,
  questionTypes: [],
  bloomsTaxonomyOne: [],
  bloomsTaxonomyTwo: [],
  isAutogradeEnabled: true,
  isAutomarksEnabled: false,
  isRoundingEnabled: false,
};

const QUESTION_TYPE_OPTIONS = [
  "DESCRIPTIVE",
  "FILL_IN_BLANKS",
  "MULTI_SELECT",
  "SINGLE_SELECT",
  "UNKNOWN",
];
const BLOOMS_TAXONOMY1 = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
];
const BLOOMS_TAXONOMY2 = [
  "Factual",
  "Conceptual",
  "Procedural",
  "Metacognitive",
];

function CreateExamPaperPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<CreateExamInput>(DEFAULT_INPUT);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [createExamPaper, { loading: creating }] = useMutation(
    CREATE_EXAM_PAPER,
    {
      client: autoGradeClient,
    }
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);

  const isValid =
    form.title.trim() !== "" &&
    form.scheduledDate !== null &&
    form.totalSections > 0 &&
    form.totalMarks > 0 &&
    form.totalQuestions > 0 &&
    form.questionTypes.length > 0;

  const handleChange = (field: keyof CreateExamInput, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const [errorMessage, setErrorMessage] = useState("");
  const [openError, setOpenError] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createExamPaper({
        variables: { input: form },
      });
      setSuccessMessage("Exam paper created successfully!");
      setOpenSuccess(true);
      setTimeout(() => {
        history.push("/ai-evaluation/exam-paper");
      }, 2000);
    } catch (error: any) {
      console.error("Failed to create exam paper", error);
      setErrorMessage(error?.message || "Something went wrong!");
      setOpenError(true);
    }
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">Create Exam Paper</Typography>
          </Box>
          <Grid container spacing={2} component="form" onSubmit={handleSave}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <UploadBox
                  setUserAnswerAsPdf={(url: string) =>
                    handleChange("questionPaperPublicPdfUrl", url)
                  }
                  filename={uploadedFileName}
                  setFilename={setUploadedFileName}
                  usecase="test_paper_upload"
                  acceptedFormats={["pdf"]}
                  presignedUrlQuery={GET_PRESIGNED_URL}
                  client={autoGradeClient}
                  placeholderText="Click to choose question paper"
                />
                {form.questionPaperPublicPdfUrl && (
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Question Paper PDF URL"
                    value={form.questionPaperPublicPdfUrl}
                    onChange={(e) =>
                      handleChange("questionPaperPublicPdfUrl", e.target.value)
                    }
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledDate || ""}
                onChange={(e) => {
                  // e.target.value will be in "YYYY-MM-DD" format or empty string
                  handleChange("scheduledDate", e.target.value || null);
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Sections"
                type="number"
                value={form.totalSections}
                onChange={(e) =>
                  handleChange("totalSections", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Marks"
                type="number"
                value={form.totalMarks}
                onChange={(e) =>
                  handleChange("totalMarks", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Questions"
                type="number"
                value={form.totalQuestions}
                onChange={(e) =>
                  handleChange("totalQuestions", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={8} marginTop={5}>
              <FormControl fullWidth required>
                <InputLabel>Question Types</InputLabel>
                <Select
                  multiple
                  value={form.questionTypes}
                  onChange={(e) =>
                    handleChange("questionTypes", e.target.value)
                  }
                  input={<OutlinedInput label="Question Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy One</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyOne as unknown as string[]}
                  onChange={(e) =>
                    handleChange(
                      "bloomsTaxonomyOne",
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Bloom's Taxonomy One" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {BLOOMS_TAXONOMY1.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy Two</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyTwo as unknown as string[]}
                  onChange={(e) =>
                    handleChange(
                      "bloomsTaxonomyTwo",
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Bloom's Taxonomy Two" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {BLOOMS_TAXONOMY2.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isAutogradeEnabled}
                    onChange={(e) =>
                      handleChange("isAutogradeEnabled", e.target.checked)
                    }
                  />
                }
                label="Enable Auto-grade"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isAutomarksEnabled}
                    onChange={(e) =>
                      handleChange("isAutomarksEnabled", e.target.checked)
                    }
                  />
                }
                label="Enable Auto-marks"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isRoundingEnabled}
                    onChange={(e) =>
                      handleChange("isRoundingEnabled", e.target.checked)
                    }
                  />
                }
                label="Is Rounding Enabled"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => history.goBack()}
                  style={{ marginRight: 8 }}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isValid || creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Snackbar
        open={openError}
        autoHideDuration={4000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenError(false)}
          severity="error"
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={openSuccess}
        autoHideDuration={2000}
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSuccess(false)}
          severity="success"
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default CreateExamPaperPage;
